/**
 * Dorellini PDP Flavor Swap
 * ------------------------------------------------------------
 * Intercepts clicks on .dor-pdp-flavor-pill anchors. Instead of a
 * full page navigation, fetches the new product's `main` section
 * via Shopify's Section Rendering API and swaps it into the DOM.
 * Falls back to a normal navigation on any error.
 *
 * Loaded as a deferred asset from layout/theme.liquid for product
 * templates only. Survives section swaps because it lives outside
 * the swapped DOM. Uses click delegation on `document` so it auto-
 * binds to pills inside any freshly-swapped section.
 *
 * Key facts (confirmed by curl-inspecting the rendered PDP):
 *  - The "main" section is rendered as a <div id="shopify-section-
 *    template--{themeId}__main" class="shopify-section">…</div>
 *    NOT a <section> element.
 *  - The Section Rendering API requires the FULL ID without the
 *    "shopify-section-" prefix:
 *      ?sections=template--{themeId}__main
 *    The simple key "main" returns {"main": null}.
 */
(function () {
  if (typeof window === 'undefined') return;

  var inFlight = false;

  function findMainSectionEl() {
    return document.querySelector('[id^="shopify-section-"][id$="__main"]');
  }

  function getSectionApiId(el) {
    // strip "shopify-section-" prefix
    return el.id.replace(/^shopify-section-/, '');
  }

  function updateDocumentTitle(targetUrl) {
    return fetch(targetUrl, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var match = html.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
        if (match && match[1]) document.title = match[1].trim();
      })
      .catch(function () { /* non-critical */ });
  }

  function reExecuteScripts(root) {
    // Scripts inserted via innerHTML do NOT execute. Clone each one
    // into a fresh <script> element so the browser parses + runs it.
    root.querySelectorAll('script').forEach(function (oldScript) {
      var s = document.createElement('script');
      for (var i = 0; i < oldScript.attributes.length; i++) {
        s.setAttribute(oldScript.attributes[i].name, oldScript.attributes[i].value);
      }
      s.text = oldScript.textContent;
      oldScript.parentNode.replaceChild(s, oldScript);
    });
  }

  function swapMainSection(targetUrl, busyEl) {
    console.log('[dor-swap] swapMainSection start', { targetUrl: targetUrl, busyEl: busyEl });
    if (inFlight) { console.log('[dor-swap] aborted: already in flight'); return; }
    var currentMain = findMainSectionEl();
    console.log('[dor-swap] currentMain:', currentMain && currentMain.id);
    if (!currentMain) {
      console.warn('[dor-swap] no main section in DOM, falling back to hard nav');
      window.location.href = targetUrl;
      return;
    }
    inFlight = true;
    if (busyEl) {
      busyEl.style.opacity = '0.5';
      busyEl.setAttribute('aria-busy', 'true');
    }

    var sectionApiId = getSectionApiId(currentMain);
    var sep = targetUrl.indexOf('?') >= 0 ? '&' : '?';
    var sectionUrl = targetUrl + sep + 'sections=' + encodeURIComponent(sectionApiId);
    console.log('[dor-swap] fetching:', sectionUrl);

    fetch(sectionUrl, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (response) {
        console.log('[dor-swap] fetch response status:', response.status);
        if (!response.ok) throw new Error('Section fetch HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        console.log('[dor-swap] response keys:', Object.keys(data || {}));
        /* Shopify normalizes Section Rendering API response keys. The
           requested key (template--XXX__main) may come back keyed
           simply as "product" or "main". Try the requested key first,
           then fall back to the first key present in the envelope. */
        var html = data && data[sectionApiId];
        if (!html && data) {
          var keys = Object.keys(data);
          if (keys.length > 0) {
            console.log('[dor-swap] requested key not in response — using fallback key:', keys[0]);
            html = data[keys[0]];
          }
        }
        if (!html) throw new Error('No section HTML in response. Keys: ' + Object.keys(data || {}).join(','));
        console.log('[dor-swap] section HTML length:', html.length);

        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;

        /* The response is usually wrapped in <div id="shopify-section-…__main">,
           but some Section API responses don't wrap (or use a different
           suffix). Try most-specific → least-specific. */
        var newSection = wrapper.querySelector('[id^="shopify-section-"][id$="__main"]')
                      || wrapper.querySelector('[id^="shopify-section-"]')
                      || wrapper.firstElementChild;
        console.log('[dor-swap] newSection from response:', newSection && newSection.id);
        if (!newSection) throw new Error('No section wrapper in response HTML');

        // Swap. Horizon's custom-element connectedCallback() will run for
        // <slideshow-component>, <sticky-add-to-cart>, <product-form-component>,
        // <variant-picker>, <quantity-selector>, etc. in the new DOM.
        currentMain.replaceWith(newSection);
        console.log('[dor-swap] DOM swap complete');

        // Inline <script> tags inserted via innerHTML don't execute — re-run them.
        reExecuteScripts(newSection);

        // Update browser URL without reloading.
        window.history.pushState({ dorSwap: true, url: targetUrl }, '', targetUrl);
        console.log('[dor-swap] URL updated to', targetUrl);

        // Tell Horizon (and any listeners) the section was re-rendered.
        document.dispatchEvent(
          new CustomEvent('shopify:section:load', {
            detail: { sectionId: newSection.id, load: true },
            bubbles: true
          })
        );
        document.dispatchEvent(
          new CustomEvent('dorellini:product:swapped', {
            detail: { url: targetUrl, sectionId: newSection.id }
          })
        );

        updateDocumentTitle(targetUrl);

        // Smooth-scroll to top of the freshly-rendered section.
        var top = newSection.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      })
      .catch(function (err) {
        // Any failure → fall back to normal navigation. Never break the link.
        console.error('[dorellini] flavor swap failed — falling back to normal navigation:', err);
        window.location.href = targetUrl;
      })
      .finally(function () {
        inFlight = false;
        if (busyEl) {
          busyEl.style.opacity = '';
          busyEl.removeAttribute('aria-busy');
        }
      });
  }

  /* Expose the swap function so other PDP controls (e.g. the sticky-bar
     flavor-switcher <select>) can trigger an AJAX swap without
     duplicating the fetch/parse/replace logic. */
  window.dorellini = window.dorellini || {};
  window.dorellini.swapToFlavor = swapMainSection;

  // Click delegation on document — survives section swaps without re-binding.
  document.addEventListener('click', function (e) {
    var pill = e.target && e.target.closest ? e.target.closest('.dor-pdp-flavor-pill') : null;
    if (!pill) return;
    console.log('[dor-swap] pill click intercepted:', pill);
    // Respect modifier keys (open-in-new-tab etc.)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (pill.classList.contains('is-selected')) {
      e.preventDefault();
      return;
    }
    var targetUrl = pill.getAttribute('href');
    if (!targetUrl) return;
    e.preventDefault();
    swapMainSection(targetUrl, pill);
  });

  // Back / forward → full reload so all Horizon components fully re-hydrate.
  window.addEventListener('popstate', function (e) {
    if (e.state && e.state.dorSwap) window.location.reload();
  });
})();
